import React from 'react';
import { Box, InputLabel, MenuItem, Typography } from '@mui/material';
import { useLocales } from '../../../../hooks';
import { makeStyles } from 'tss-react/mui';
import { Control, Controller, useWatch } from 'react-hook-form';
import FormControl from '../../FormControl';
import Select from '../../Select';
import FoldableSection from '../../FoldableSection';
import { enrichTestIds } from '../../TestsIds';
import { EnrichForm } from './Enrich';
import { DeepLinkPathField } from './DeepLinkPathField';
import { DeepLinkContentField } from './DeepLinkContentField';
import { DeepLinkEPGField } from './DeepLinkEPGField';
import { RedirectionType as EnrichRedirectionType } from '../../../../API';

interface DeepLinkProps {
  control: Control<EnrichForm>;
  isSaving: boolean;
}

export const useStyles = makeStyles()((theme) => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(6),
    margin: theme.spacing(2),
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4)
  },
  formColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
    width: '100%'
  },
  selector: {
    width: 200
  }
}));

export function DeepLink({ control, isSaving }: DeepLinkProps): JSX.Element {
  const { classes } = useStyles();
  const { t } = useLocales();
  const redirectionType = useWatch({ control, name: 'redirection.type' });

  return (
    <FoldableSection
      summary={<Typography variant="h6">{t('content.drawer.deep_link.foldable_title')}</Typography>}
      expanded
      disableGutters
    >
      <Box className={classes.form}>
        <div className={classes.formColumn}>
          <Controller
            control={control}
            name="redirection.type"
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <FormControl>
                <InputLabel id="typeSelector">{t('content.drawer.deep_link.type')}</InputLabel>
                <Select
                  label={t('content.drawer.deep_link.type')}
                  className={classes.selector}
                  labelId="typeSelector"
                  value={value}
                  onChange={({ target: { value } }) => onChange(value as string)}
                  data-testid={enrichTestIds.deepLinkSelector}
                  error={!!error}
                  disabled={isSaving}
                >
                  {Object.values(EnrichRedirectionType).map((type) => (
                    <MenuItem
                      key={type}
                      value={type}
                      data-testid={enrichTestIds.deepLinkSelectorItem}
                      data-treatment-id={type}
                    >
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
          {redirectionType === EnrichRedirectionType.PATH && (
            <DeepLinkPathField control={control} isSaving={isSaving} />
          )}
          {(redirectionType === EnrichRedirectionType.VOD || redirectionType === EnrichRedirectionType.LIVE) && (
            <DeepLinkContentField control={control} isSaving={isSaving} type={redirectionType} />
          )}
          {redirectionType === EnrichRedirectionType.EPG && <DeepLinkEPGField control={control} isSaving={isSaving} />}
        </div>
      </Box>
    </FoldableSection>
  );
}
